import { injectScript } from '../../utils/script';
import { getBrand } from '../../../components/OpenGraph/OpenGraphProductData';
import { unSlugify } from '../unslugify';
import { breadcrumbsWithoutProduct } from '../../utils/breadcrumbsWithoutProduct';
import { CONSENT_COOKIE_NAME, defaultConsentSettings } from '../../AnalyticsProvider';
import { Cookies } from 'react-cookie';
const ua = () => window.gtag;
function getProductData({ product, selectedVariation, useArticleNumberAsId = false, brandCustomField, category = '' }) {
    const brand = getBrand(product, brandCustomField);
    if (useArticleNumberAsId) {
        return {
            id: (selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.articleNumber) || product.articleNumber,
            productId: product.id,
            productSKU: product.articleNumber,
            variant: selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.values.join('_'),
            variantSKU: selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.articleNumber,
            name: product.name,
            base_product: product.articleNumber,
            category,
            brand
        };
    }
    else {
        // This is kept as backwards compatibility. Some shops rely on the product id being the internal Jetshop id, instead of the article number.
        // Changing this could affect the statistics in Google Ads/Merchant/Analytics, so we want the new behaviour to be opt-in.
        return {
            id: product.id,
            variant: (selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.articleNumber) || product.articleNumber,
            name: product.name,
            base_prduct: product.articleNumber,
            category,
            brand
        };
    }
}
export const gtagTracker = ({ trackingId, additionalGtagTrackingIds = [], currency, brandCustomField, useArticleNumberAsId = false }) => ({
    name: 'gtag',
    initBrowser({ config }) {
        const w = window;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
            originalLocation: document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search
        });
        w.gtag = function gtag() {
            w.dataLayer.push(arguments); // I'm not sure why but this dosen't work using rest params. Don't change this line
        };
        if (config.useTrackingConsentAPI) {
            const consents = config.cookies.get(CONSENT_COOKIE_NAME) || defaultConsentSettings;
            // NB: Only cookieless pings will be sent to Google servers.
            // There’s also a wait time of 500 milliseconds before any Google tags are allowed to fire,
            // to give time for the update command to be invoked.
            ua()('consent', 'default', Object.assign({}, consents));
            // NB: This only works when you set ad_storage to denied.
            // https://developers.google.com/tag-platform/devguides/consent#redact_ads_data
            ua()('set', 'ads_data_redaction', true);
        }
        w.gtag('js', new Date());
        ua()('config', trackingId, {
            currency,
            send_page_view: false
        });
        additionalGtagTrackingIds.forEach((id) => {
            ua()('config', id, {
                currency,
                send_page_view: false
            });
        });
        injectScript({
            src: `https://www.googletagmanager.com/gtag/js?id=${trackingId}`
        });
        ua();
    },
    consent(consents) {
        const cookies = new Cookies();
        // Fallback to all granted if no consents are passed
        const consent = consents
            ? consents
            : {
                ad_storage: 'granted',
                analytics_storage: 'granted',
                personalization_storage: 'granted',
                functionality_storage: 'granted',
                security_storage: 'granted'
            };
        ua()('consent', 'update', Object.assign({}, consent));
        cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(Object.assign({}, consent)));
    },
    trackProduct({ quantity, action, product, selectedVariation, list, listName, index, category }, context, eventId) {
        const productData = Object.assign(Object.assign({}, getProductData({
            product,
            selectedVariation,
            useArticleNumberAsId,
            brandCustomField,
            category
        })), { price: (selectedVariation && selectedVariation.price.incVat) ||
                (product.price && product.price.incVat), quantity, list_name: list || listName || context.list, list_position: index });
        switch (action) {
            case 'click':
                ua()('event', 'select_content', {
                    currency,
                    content_type: 'product',
                    items: [productData],
                    eventId
                });
                break;
            case 'view':
                ua()('event', 'view_item', {
                    currency,
                    items: [productData],
                    non_interaction: true,
                    eventId
                });
                break;
            case 'add':
                ua()('event', 'add_to_cart', {
                    currency,
                    items: [productData],
                    eventId
                });
                break;
            case 'remove':
                ua()('event', 'remove_from_cart', {
                    currency,
                    items: [productData],
                    eventId
                });
                break;
            default:
                console.log('Error: Gtag can not track action ' + action);
                break;
        }
    },
    trackCartCheckout({ cart }, _context, eventId) {
        const items = cart.items.map((item) => {
            var _a, _b, _c;
            return (Object.assign(Object.assign({}, getProductData({
                product: item.product,
                selectedVariation: item.variant,
                useArticleNumberAsId,
                brandCustomField,
                category: breadcrumbsWithoutProduct((_a = item.product.primaryRoute) === null || _a === void 0 ? void 0 : _a.breadcrumbs) ||
                    unSlugify((_c = (_b = item.product.canonicalCategory) === null || _b === void 0 ? void 0 : _b.primaryRoute) === null || _c === void 0 ? void 0 : _c.path) ||
                    ''
            })), { price: item.total.incVat, quantity: item.quantity }));
        });
        ua()('event', 'begin_checkout', {
            currency,
            items,
            eventId
        });
    },
    trackPageView({ pathname, title }, _context, eventId) {
        ua()('event', 'page_view', {
            currency,
            page_path: pathname,
            page_title: title,
            eventId
        });
    },
    trackList({ listName, products, category }, _context, eventId) {
        const productData = products.map((product, i) => {
            var _a, _b, _c, _d;
            return (Object.assign(Object.assign({}, getProductData({
                product,
                selectedVariation: null,
                useArticleNumberAsId,
                brandCustomField,
                category: category ||
                    breadcrumbsWithoutProduct((_a = product === null || product === void 0 ? void 0 : product.primaryRoute) === null || _a === void 0 ? void 0 : _a.breadcrumbs) ||
                    unSlugify((_c = (_b = product === null || product === void 0 ? void 0 : product.canonicalCategory) === null || _b === void 0 ? void 0 : _b.primaryRoute) === null || _c === void 0 ? void 0 : _c.path)
            })), { price: (_d = product.price) === null || _d === void 0 ? void 0 : _d.incVat, list_name: listName, list_position: i }));
        });
        ua()('event', 'view_item_list', {
            currency,
            items: productData,
            non_interaction: true,
            eventId
        });
    },
    trackExperiment({ id, variant }, _context, eventId) {
        ua()('config', trackingId, {
            experiments: [{ id, variant }],
            eventId
        });
    },
    trackUser({ userId }, _context, eventId) {
        ua()('event', 'authentication', { user_id: userId, eventId });
    },
    trackLogin(_props, _context, eventId) {
        ua()('event', 'login', { trackingId, eventId });
    },
    trackRegister(_props, _context, eventId) {
        ua()('event', 'sign_up', { trackingId, eventId });
    },
    trackAddToWishList({ articleNumber, productName }, _context, eventId) {
        ua()('event', 'add_to_wishlist', {
            articleNumber,
            productName,
            eventId
        });
    }
});
//# sourceMappingURL=gtag.js.map